import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth, useGuardOr } from '../../hooks/auth'
import { EventInfo, useEvent } from '../../hooks/events'
import { LOCAL_EXECUTION } from '../../utils/featureFlag'
import {
  deviceLabelFromToken,
  loadSelection,
  saveSelection,
  SearchTicket,
  searchTickets,
  TicketInfo,
  useOccasions,
  useSectors,
  useValidationHistory,
  validateTicket,
  ValidationResult,
} from '../../hooks/ticket-validation'
import { CameraScanner } from '../../components/CameraScanner'
import { SectorFilter } from './SectorFilter'
import {
  Actions,
  BackBar,
  BatchBar,
  Btn,
  CheckBox,
  Chip,
  ConfigBar,
  ConfigRow,
  ConfirmBackdrop,
  ConfirmCard,
  Detected,
  DevicePill,
  Empty,
  EventName,
  FilterBody,
  FilterHeader,
  FilterOverlay,
  FilterRow,
  HelpButton,
  Hint,
  HistoryToggle,
  List,
  OccasionBar,
  Or,
  ResultCard,
  ResultHero,
  Row,
  ScanButton,
  SearchField,
  SectionTitle,
  TicketCard,
  TopRow,
  Wrap,
} from './styles'

type View = 'home' | 'scanner' | 'results' | 'result' | 'batch-result'

interface BatchItem {
  chave: string
  ingresso: string
  ok: boolean
  mensagem?: string
}

// Classificação do termo (chip informativo — o back também classifica).
function classify(value: string): string {
  const s = (value || '').trim()
  const digits = s.replace(/\D/g, '')
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)) return 'E-mail'
  if (digits.length === 11 && /^[\d.\-\s]+$/.test(s)) return 'CPF'
  if (/^\d{1,10}$/.test(s.replace(/\s/g, ''))) return 'Pedido'
  if (/[a-z]/i.test(s) && /^[a-zà-ú\s'.-]+$/i.test(s)) return 'Nome'
  return 'Bilhete'
}

const fmtTime = (iso?: string) => {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return ''
  }
}
const fmtFull = (iso?: string) => {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return ''
  }
}

// ingressos.Tipo -> rótulo legível
const formatTipo = (v?: string): string => {
  const s = (v || '').trim()
  const up = s.toUpperCase()
  if (up === 'M') return 'Meia'
  if (up === 'I') return 'Inteira'
  return s || '—'
}

const chipTone = (status: string): 'ok' | 'warn' | 'bad' => {
  if (status === 'DISPONIVEL') return 'ok'
  if (status === 'UTILIZADO') return 'warn'
  return 'bad'
}

const chipLabel = (status: string): string => {
  if (status === 'DISPONIVEL') return 'Disponível'
  if (status === 'UTILIZADO') return 'Já utilizado'
  return 'Cancelado'
}

type ResultTone = 'valid' | 'used' | 'bad'

// Estado visual do resultado — funções puras (uma responsabilidade, fáceis de debugar).
function resultTone(result: ValidationResult): ResultTone {
  if (result.valido) return 'valid'
  if (result.motivo === 'USED') return 'used'
  return 'bad'
}

function resultTitle(result: ValidationResult, reviewMode: boolean): string {
  if (reviewMode) return 'Detalhes da validação'
  if (result.valido) return 'Ingresso válido'
  return result.mensagem || 'Ingresso inválido'
}

function resultSubtitle(result: ValidationResult, reviewMode: boolean): string {
  if (reviewMode) return `Validado às ${fmtTime(result.horario)}`
  if (result.valido) return `Validado agora · ${fmtTime(result.horario)}`
  if (result.motivo === 'USED') return `Já validado em ${fmtFull(result.validadoEm)}`
  return ''
}

function selectionSummary(
  total: number,
  selectedCount: number,
  isAll: boolean
): string {
  if (total === 0) return 'Todos'
  if (isAll) return 'Todos os ingressos'
  if (selectedCount === 0) return 'Nenhum ingresso'
  return `${selectedCount} de ${total} ingressos`
}

export function TicketValidator({ permissions = ['DP18'] }) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { authHookWithEvent } = useAuth()

  if (!LOCAL_EXECUTION) {
    authHookWithEvent(id as any, navigate)
  }
  useGuardOr(permissions)

  const fetchEvent = useEvent(Number(id))
  const event = useMemo(
    () => (fetchEvent.event || {}) as EventInfo,
    [fetchEvent]
  )
  const { occasions } = useOccasions(id)
  const { history, refreshHistory } = useValidationHistory(id)
  const { eventos: eventGroups } = useSectors(id)

  const [view, setView] = useState<View>('home')
  const [ocasiao, setOcasiao] = useState(0)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchTicket[]>([])
  const [result, setResult] = useState<ValidationResult | null>(null)
  const [reviewMode, setReviewMode] = useState(false)
  const [busy, setBusy] = useState(false)
  const [feedback, setFeedback] = useState('')
  const [showFilter, setShowFilter] = useState(false)
  const [showEnabled, setShowEnabled] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number> | null>(null)
  const [batchSelected, setBatchSelected] = useState<Set<string>>(new Set())
  const [confirmBatch, setConfirmBatch] = useState(false)
  const [batchResults, setBatchResults] = useState<BatchItem[] | null>(null)

  const deviceLabel = deviceLabelFromToken()

  const allTicketIds = useMemo(
    () =>
      eventGroups.flatMap((ev) =>
        ev.setores.flatMap((s) => s.ingressos.map((i) => i.ingressoId))
      ),
    [eventGroups]
  )

  // Inicializa a seleção quando os setores carregam (localStorage ou "todos").
  useEffect(() => {
    if (selectedIds !== null || allTicketIds.length === 0) return
    const saved = id ? loadSelection(id) : null
    if (saved) {
      setSelectedIds(new Set(saved.filter((x) => allTicketIds.includes(x))))
    } else {
      setSelectedIds(new Set(allTicketIds))
    }
  }, [allTicketIds, id, selectedIds])

  const effectiveSelected = selectedIds ?? new Set(allTicketIds)
  const isAllSelected =
    allTicketIds.length > 0 &&
    allTicketIds.every((tid) => effectiveSelected.has(tid))
  // undefined = sem filtro (valida tudo); array = aplica filtro
  const permitidos = isAllSelected ? undefined : Array.from(effectiveSelected)

  const applySelection = (ids: number[]) => {
    setSelectedIds(new Set(ids))
    if (id) saveSelection(id, ids)
    setShowFilter(false)
  }

  // Ingressos habilitados (para o modal "?") — só os selecionados no filtro.
  const enabledGroups = eventGroups
    .map((ev) => ({
      ...ev,
      setores: ev.setores
        .map((s) => ({
          ...s,
          ingressos: s.ingressos.filter((i) =>
            effectiveSelected.has(i.ingressoId)
          ),
        }))
        .filter((s) => s.ingressos.length > 0),
    }))
    .filter((ev) => ev.setores.length > 0)

  const goHome = () => {
    setView('home')
    setResult(null)
    setResults([])
    setReviewMode(false)
    setFeedback('')
    setBatchSelected(new Set())
    setConfirmBatch(false)
    setBatchResults(null)
  }

  const toggleBatch = (chave: string) => {
    setBatchSelected((prev) => {
      const next = new Set(prev)
      if (next.has(chave)) next.delete(chave)
      else next.add(chave)
      return next
    })
  }

  // Valida em lote os ingressos selecionados (sequencial — o device token é
  // lido/gravado a cada chamada; paralelo geraria corrida no localStorage).
  const runBatch = async () => {
    if (!id || busy || batchSelected.size === 0) return
    setBusy(true)
    setConfirmBatch(false)
    try {
      const out: BatchItem[] = []
      for (const chave of Array.from(batchSelected)) {
        const info = results.find((r) => r.chave === chave)
        try {
          const r = await validateTicket(id, chave, ocasiao, permitidos)
          out.push({
            chave,
            ingresso: info?.ingresso ?? chave,
            ok: r.valido,
            mensagem: r.valido ? undefined : r.mensagem,
          })
        } catch {
          out.push({
            chave,
            ingresso: info?.ingresso ?? chave,
            ok: false,
            mensagem: 'Erro ao validar',
          })
        }
      }
      setBatchResults(out)
      setView('batch-result')
      refreshHistory()
    } finally {
      setBusy(false)
    }
  }

  const doValidate = async (code: string) => {
    if (!id || busy) return
    setBusy(true)
    setFeedback('')
    try {
      const r = await validateTicket(id, code, ocasiao, permitidos)
      setResult(r)
      setReviewMode(false)
      setView('result')
      refreshHistory()
    } catch {
      setFeedback('Não foi possível validar. Tente novamente.')
      setView('home')
    } finally {
      setBusy(false)
    }
  }

  const handleScan = (code: string) => {
    doValidate(code)
  }

  const handleSearch = async () => {
    if (!id || !query.trim() || busy) return
    setBusy(true)
    setFeedback('')
    try {
      const { ingressos } = await searchTickets(id, query.trim())
      if (ingressos.length === 0) {
        setFeedback('Nenhum ingresso encontrado.')
      } else if (ingressos.length === 1) {
        await doValidate(ingressos[0].chave)
      } else {
        setResults(ingressos)
        setView('results')
      }
    } catch {
      setFeedback('Erro na busca. Tente novamente.')
    } finally {
      setBusy(false)
    }
  }

  const openReview = (item: (typeof history)[number]) => {
    setResult({
      success: true,
      valido: true,
      motivo: null,
      horario: item.dataHora,
      ingresso: {
        chave: item.chave,
        nome: item.nome || '—',
        ingresso: item.ingresso,
        setor: item.setor,
        ocasiao: item.ocasiao,
      },
    })
    setReviewMode(true)
    setView('result')
  }

  // ---- Configuração de ingressos/setores (overlay) ----
  if (showFilter) {
    return (
      <SectorFilter
        eventos={eventGroups}
        selected={Array.from(effectiveSelected)}
        onApply={applySelection}
        onClose={() => setShowFilter(false)}
      />
    )
  }

  // ---- Ingressos habilitados (modal read-only) ----
  if (showEnabled) {
    return (
      <FilterOverlay>
        <FilterHeader>
          <h3>Ingressos habilitados</h3>
          <button
            className="x"
            aria-label="Fechar"
            onClick={() => setShowEnabled(false)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </FilterHeader>
        <FilterBody>
          <Hint style={{ margin: '4px 4px 12px' }}>
            {isAllSelected
              ? 'Este validador está lendo TODOS os ingressos.'
              : 'Este validador só valida os ingressos abaixo.'}
          </Hint>
          {enabledGroups.length === 0 ? (
            <Empty>Nenhum ingresso habilitado.</Empty>
          ) : (
            enabledGroups.map((ev) => (
              <div key={ev.eventoId}>
                <FilterRow as="div" $level={0}>
                  <span className="label">{ev.evento}</span>
                </FilterRow>
                {ev.setores.map((s) => (
                  <div key={s.setorId}>
                    <FilterRow as="div" $level={1}>
                      <span className="label">{s.setor}</span>
                      <span className="count">{s.ingressos.length}</span>
                    </FilterRow>
                    {s.ingressos.map((i) => (
                      <FilterRow as="div" key={i.ingressoId} $level={2}>
                        <span className="label">{i.ingresso}</span>
                      </FilterRow>
                    ))}
                  </div>
                ))}
              </div>
            ))
          )}
        </FilterBody>
      </FilterOverlay>
    )
  }

  // ---- Scanner (overlay full-screen) ----
  if (view === 'scanner') {
    return (
      <CameraScanner
        subtitle={`${event?.nome ?? ''} · Ocasião ${ocasiao}`}
        onScan={handleScan}
        onClose={goHome}
        onManual={goHome}
      />
    )
  }

  // ---- Resultado ----
  if (view === 'result' && result) {
    const tone = resultTone(result)
    const title = resultTitle(result, reviewMode)
    const sub = resultSubtitle(result, reviewMode)
    const t = result.ingresso

    return (
      <Wrap>
        <BackBar onClick={goHome}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
          Voltar
        </BackBar>

        <ResultHero tone={tone}>
          <div className="badge">
            <ResultBadgeIcon tone={tone} />
          </div>
          <h2>{title}</h2>
          {sub && <p className="sub">{sub}</p>}
        </ResultHero>

        {t && (
          <ResultCard>
            <ResultRows
              ticket={t}
              valid={result.valido}
              reviewMode={reviewMode}
              deviceLabel={deviceLabel}
            />
          </ResultCard>
        )}

        <Actions>
          <ResultActions
            result={result}
            reviewMode={reviewMode}
            onNext={() => setView('scanner')}
            onHome={goHome}
          />
        </Actions>
      </Wrap>
    )
  }

  // ---- Seleção múltipla de ingressos ----
  if (view === 'results') {
    return (
      <Wrap>
        <BackBar onClick={goHome}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
          Voltar
        </BackBar>
        <SectionTitle>
          <h3>{results.length} ingressos encontrados</h3>
        </SectionTitle>
        <Hint style={{ marginBottom: 14 }}>
          Selecione os ingressos que deseja validar.
        </Hint>
        <List style={{ paddingBottom: 88 }}>
          {results.map((tk) => {
            const disabled = tk.status !== 'DISPONIVEL'
            const selected = batchSelected.has(tk.chave)
            return (
              <TicketCard
                key={tk.bilheteId}
                $selected={selected}
                onClick={() => !disabled && toggleBatch(tk.chave)}
                disabled={disabled || busy}
              >
                <div className="top">
                  <span className="nm">{tk.ingresso}</span>
                  {disabled ? (
                    <Chip tone={chipTone(tk.status)}>
                      <span className="d" />
                      {chipLabel(tk.status)}
                    </Chip>
                  ) : (
                    <CheckBox $state={selected ? 'on' : 'off'}>
                      {selected && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      )}
                    </CheckBox>
                  )}
                </div>
                <div className="grid">
                  <div>
                    <div className="k">Nome</div>
                    <div className="v">{tk.nome}</div>
                  </div>
                  <div>
                    <div className="k">Setor</div>
                    <div className="v">{tk.setor}</div>
                  </div>
                  <div>
                    <div className="k">Bilhete</div>
                    <div className="v">{tk.chave}</div>
                  </div>
                </div>
              </TicketCard>
            )
          })}
        </List>

        {batchSelected.size > 0 && (
          <BatchBar>
            <div>
              <Btn
                variant="primary"
                onClick={() => setConfirmBatch(true)}
                disabled={busy}
              >
                Validar {batchSelected.size} ingresso
                {batchSelected.size > 1 ? 's' : ''}
              </Btn>
            </div>
          </BatchBar>
        )}

        {confirmBatch && (
          <ConfirmBackdrop onClick={() => setConfirmBatch(false)}>
            <ConfirmCard onClick={(e) => e.stopPropagation()}>
              <h3>Confirmar validação</h3>
              <p>
                Você vai validar {batchSelected.size} ingresso
                {batchSelected.size > 1 ? 's' : ''}. Deseja continuar?
              </p>
              <div className="row">
                <Btn variant="ghost" onClick={() => setConfirmBatch(false)}>
                  Cancelar
                </Btn>
                <Btn variant="primary" onClick={runBatch} disabled={busy}>
                  {busy ? 'Validando…' : 'Confirmar'}
                </Btn>
              </div>
            </ConfirmCard>
          </ConfirmBackdrop>
        )}
      </Wrap>
    )
  }

  // ---- Resultado do lote ----
  if (view === 'batch-result' && batchResults) {
    const okCount = batchResults.filter((r) => r.ok).length
    const failCount = batchResults.length - okCount
    const tone: ResultTone =
      failCount === 0 ? 'valid' : okCount === 0 ? 'bad' : 'used'
    const fails = batchResults.filter((r) => !r.ok)
    return (
      <Wrap>
        <ResultHero tone={tone}>
          <div className="badge">
            <ResultBadgeIcon tone={tone} />
          </div>
          <h2>
            {okCount} validado{okCount !== 1 ? 's' : ''}
          </h2>
          {failCount > 0 && (
            <p className="sub">
              {failCount} não validado{failCount > 1 ? 's' : ''}
            </p>
          )}
        </ResultHero>
        {fails.length > 0 && (
          <ResultCard>
            {fails.map((r) => (
              <div className="rrow" key={r.chave}>
                <span className="ico">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </span>
                <div>
                  <div className="k">{r.ingresso}</div>
                  <div className="v">{r.mensagem || 'Não validado'}</div>
                </div>
              </div>
            ))}
          </ResultCard>
        )}
        <Actions>
          <Btn variant="primary" onClick={goHome}>
            Concluir
          </Btn>
        </Actions>
      </Wrap>
    )
  }

  // ---- Home ----
  const detected = query.trim() ? classify(query) : ''

  return (
    <Wrap>
      <TopRow>
        <EventName>
          <span>Validador</span>
          <h1>{event?.nome ?? 'Evento'}</h1>
        </EventName>
        <DevicePill title="Identificador deste dispositivo">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <rect x="6" y="2" width="12" height="20" rx="3" />
            <path d="M11 18h2" />
          </svg>
          {deviceLabel}
        </DevicePill>
      </TopRow>

      <OccasionBar>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1B4DD8" strokeWidth="2" strokeLinecap="round">
          <rect x="3" y="4" width="18" height="17" rx="2" />
          <path d="M8 2v4M16 2v4M3 10h18" />
        </svg>
        <span className="lab">Ocasião</span>
        <select
          value={ocasiao}
          onChange={(e) => setOcasiao(Number(e.target.value))}
          aria-label="Ocasião"
        >
          {(occasions.length
            ? occasions
            : [{ numero: 0, nome: 'Padrão (evento único)' }]
          ).map((o) => (
            <option key={o.numero} value={o.numero}>
              {o.numero} · {o.nome}
            </option>
          ))}
        </select>
      </OccasionBar>

      {allTicketIds.length > 0 && (
        <ConfigRow>
          <ConfigBar onClick={() => setShowFilter(true)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1B4DD8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18M7 12h10M11 18h2" />
            </svg>
            <span className="lab">Validando</span>
            <span className="val">
              {selectionSummary(
                allTicketIds.length,
                effectiveSelected.size,
                isAllSelected
              )}
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M9 6l6 6-6 6" />
              </svg>
            </span>
          </ConfigBar>
          <HelpButton
            onClick={() => setShowEnabled(true)}
            aria-label="Ver ingressos habilitados"
            title="Ingressos habilitados para leitura"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M9.6 9a2.4 2.4 0 1 1 3.4 2.2c-.7.4-1 .9-1 1.8M12 17h.01" />
            </svg>
          </HelpButton>
        </ConfigRow>
      )}

      <ScanButton onClick={() => setView('scanner')} disabled={busy}>
        <span className="ico">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2" />
            <path d="M7 12h10" />
          </svg>
        </span>
        <strong>Ler QR Code</strong>
        <span>Aponte a câmera para o ingresso</span>
      </ScanButton>

      <Or>ou pesquise</Or>

      <SearchField>
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#8494AE" strokeWidth="2" strokeLinecap="round">
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="CPF, Pedido, Nome, E-mail ou Bilhete"
          autoComplete="off"
          inputMode="search"
        />
      </SearchField>
      {detected ? (
        <Detected>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1B4DD8" strokeWidth="2.4" strokeLinecap="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
          Detectado: <b>{detected}</b>
        </Detected>
      ) : (
        <Hint>
          Identificamos o tipo automaticamente e buscamos só nos seus eventos.
        </Hint>
      )}
      {feedback && (
        <Hint style={{ color: '#C77700', fontWeight: 600 }}>{feedback}</Hint>
      )}

      <HistoryToggle
        $open={showHistory}
        onClick={() => setShowHistory((v) => !v)}
        aria-expanded={showHistory}
      >
        <h3>Últimos validados</h3>
        {history.length > 0 && <span className="count">{history.length}</span>}
        <span className="chev">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </span>
      </HistoryToggle>
      {showHistory &&
        (history.length === 0 ? (
          <Empty>Nenhuma validação ainda.</Empty>
        ) : (
          <List>
            {history.map((it, i) => (
              <Row key={`${it.chave}-${i}`} onClick={() => openReview(it)}>
              <span className="time">{it.horario}</span>
              <span className="ico">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 9V7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2a2 2 0 0 0 0 6v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-6z" />
                  <path d="M13 5v14" />
                </svg>
              </span>
              <span className="who">
                <span className="nm">{it.chave}</span>
                <span className="mt">{it.ingresso}</span>
              </span>
              <span className="chev">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </span>
              </Row>
            ))}
          </List>
        ))}
    </Wrap>
  )
}

function ResultRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="rrow">
      <span className="ico">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <circle cx="12" cy="12" r="9" />
        </svg>
      </span>
      <div>
        <div className="k">{k}</div>
        <div className="v">{v}</div>
      </div>
    </div>
  )
}

// Ícone do badge por estado do resultado.
function ResultBadgeIcon({ tone }: { tone: ResultTone }) {
  if (tone === 'valid') {
    return (
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 6L9 17l-5-5" />
      </svg>
    )
  }
  if (tone === 'used') {
    return (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.3" strokeLinecap="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    )
  }
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  )
}

// Linhas do comprovante — histórico e validação têm campos diferentes.
function ResultRows({
  ticket,
  valid,
  reviewMode,
  deviceLabel,
}: {
  ticket: TicketInfo
  valid: boolean
  reviewMode: boolean
  deviceLabel: string
}) {
  if (reviewMode) {
    return (
      <>
        <ResultRow k="Ingresso" v={ticket.ingresso || '—'} />
        {ticket.setor && <ResultRow k="Setor" v={ticket.setor} />}
        <ResultRow k="Bilhete" v={ticket.chave} />
      </>
    )
  }
  return (
    <>
      <ResultRow k="Ingresso" v={ticket.nome || '—'} />
      <ResultRow k="Tipo" v={formatTipo(ticket.ingresso)} />
      {ticket.setor && <ResultRow k="Setor" v={ticket.setor} />}
      <ResultRow k="Bilhete" v={ticket.chave} />
      {valid && <ResultRow k="Validado no dispositivo" v={deviceLabel} />}
    </>
  )
}

// Ações do rodapé — histórico só fecha; validação segue para a próxima leitura.
function ResultActions({
  result,
  reviewMode,
  onNext,
  onHome,
}: {
  result: ValidationResult
  reviewMode: boolean
  onNext: () => void
  onHome: () => void
}) {
  if (reviewMode) {
    return (
      <Btn variant="ghost" onClick={onHome}>
        Fechar
      </Btn>
    )
  }
  const nextLabel = result.valido ? 'Próxima leitura' : 'Ler próximo ingresso'
  return (
    <>
      <Btn variant="primary" onClick={onNext}>
        {nextLabel}
      </Btn>
      <Btn variant="ghost" onClick={onHome}>
        Voltar ao início
      </Btn>
    </>
  )
}

export default TicketValidator
